import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  flip,
  shift,
  offset,
  autoUpdate,
  type Placement,
} from '@floating-ui/react'
import { TaskDropdownProps } from './TaskDropdown.types'
import { DateSheetShell } from '../DateSheetShell'
import { ListFocusContext } from '../../utils/listFocus'

const TITLEBAR_HEIGHT = 48
const VIEWPORT_PADDING = 8

// Below this available body width the 500px date popover can't fit — open the
// stacked bottom sheet instead. Covers list mode (412px) + future stack mode;
// full mode (≥784px) and a widened list window stay on the popover.
const SHEET_WIDTH_THRESHOLD = 520

/** Ist unter dem Auslöser kein Platz, weicht ein Kasten zur Seite aus. */
const SIDEWAYS: Placement[] = ['right-start', 'left-start']
const NOWHERE: Placement[] = []

/**
 * **Die Kästen darin gehen gerade unter ihrem Auslöser auf** — die Schnelleingabe (Sven, 04.10.2026). Ihr
 * Fenster wächst nach unten, sobald ein Kasten aufgeht (`useQuickAddWindowHeight`). Ohne das wich ein Kasten
 * zur Seite aus, weil er maß, als das Fenster noch klein war (Bild 42); und die Aktionsleiste hängte ihn
 * rechtsbündig an die Zeile (`alignToContainerRight`), also an den rechten Rand der Karte (Bild 43). Und sie
 * stehen linksbündig zum Auslöser (Bild 51).
 */
export const TaskDropdownBelowTrigger = createContext(false)

const COLLISION_PADDING = {
  top: TITLEBAR_HEIGHT + VIEWPORT_PADDING,
  bottom: VIEWPORT_PADDING,
  left: VIEWPORT_PADDING,
  right: VIEWPORT_PADDING,
}

export const TaskDropdown: React.FC<TaskDropdownProps> = ({
  open,
  onOpenChange,
  trigger,
  children,
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  alignOffset = 0,
  contentStyle,
  reserveBottom = 0,
  disableResize = false,
  fallbackPlacements: fallbackPlacementsProp,
  alignToContainerRight: alignToContainerRightProp,
  instant = false,
  dateSheet,
  backdrop = true,
}) => {
  const belowTrigger = useContext(TaskDropdownBelowTrigger)
  // Unter dem Auslöser heißt auch linksbündig: Mit `align="end"` der Aktionsleiste stand sonst die rechte
  // Kante am Knopf, und weiter links rutschte der Kasten an den Fensterrand (Bild 51).
  const alignment = belowTrigger ? 'start' : align
  // Mittig ist bei floating-ui die Seite ohne Zusatz: Aus „top-center“ machte es „top-start“ (#291).
  const placement = (alignment === 'center' ? side : `${side}-${alignment}`) as Placement
  const fallbackPlacements = belowTrigger ? NOWHERE : fallbackPlacementsProp ?? SIDEWAYS
  const alignToContainerRight = belowTrigger ? undefined : alignToContainerRightProp

  // Decide popover-vs-sheet once per open, based on the available content width
  // (`.main-layout__body`, which sits below the titlebar). Only date dropdowns
  // (dateSheet present) are eligible. Measured in useMemo keyed on `open` so the
  // surface doesn't switch live mid-interaction.
  const useSheet = useMemo(() => {
    if (!open || !dateSheet) return false
    const body = document.querySelector('.main-layout__body') as HTMLElement | null
    const width = body?.clientWidth ?? Infinity
    return width < SHEET_WIDTH_THRESHOLD
  }, [open, dateSheet])

  const middleware = useMemo(() => {
    const chain = [
      offset({ mainAxis: sideOffset, crossAxis: alignOffset }),
      flip({
        padding: { ...COLLISION_PADDING, bottom: COLLISION_PADDING.bottom + reserveBottom },
        fallbackPlacements,
        fallbackStrategy: 'initialPlacement',
      }),
      shift({ mainAxis: true, crossAxis: true, padding: { ...COLLISION_PADDING, bottom: COLLISION_PADDING.bottom + reserveBottom } }),
    ]
    // Custom middleware: override horizontal position to align dropdown's right edge
    // with the nearest .task-entry container, inset by alignToContainerRight pixels.
    if (alignToContainerRight != null) {
      chain.push({
        name: 'alignToContainer',
        fn({ elements, rects, placement: finalPlacement }) {
          // Only align to container when dropdown opens below/above the trigger.
          // When flipped to left/right (not enough vertical space), stay at the trigger.
          if (finalPlacement.startsWith('left') || finalPlacement.startsWith('right')) return {}
          const trigger = elements.reference as HTMLElement
          const entry = trigger.closest('.task-entry') as HTMLElement | null
          if (!entry) return {}
          const entryRect = entry.getBoundingClientRect()
          const newX = entryRect.right - alignToContainerRight - rects.floating.width
          return { x: newX }
        },
      })
    }
    return chain
  }, [sideOffset, alignOffset, reserveBottom, fallbackPlacements, alignToContainerRight])

  const { refs, floatingStyles, context, isPositioned, placement: finalPlacement } = useFloating({
    open,
    onOpenChange,
    placement,
    // Disable elementResize so the dropdown doesn't reposition when dynamic
    // content (e.g. confirm footer) appears — it grows downward instead.
    whileElementsMounted: (reserveBottom > 0 || disableResize)
      ? (reference, floating, update) => autoUpdate(reference, floating, update, { elementResize: false })
      : autoUpdate,
    middleware,
  })

  /*
    **Beim Schließen gehört die Tastatur wieder der Liste** (#284). Der Inhalt liegt im Portal; mit ihm
    geht der Fokus nach `body`, und die markierte Aufgabe hört keine Pfeile mehr. Der Aufräumer läuft bei
    `open → false` UND beim Aushängen — die Kürzel-Kästen stehen fest auf `open` und verschwinden als
    Ganzes. Ob der Auslöser in einer **zugeklappten Zeile** liegt, steht beim Öffnen fest: Beim Aushängen
    ist er schon aus dem DOM. Dropdowns in der aufgeklappten Karte, in den Details und in der
    Schnelleingabe (kein Anbieter) bleiben unberührt.
  */
  const restoreListFocus = useContext(ListFocusContext)
  const restoreListFocusRef = useRef(restoreListFocus)
  restoreListFocusRef.current = restoreListFocus
  const hasList = restoreListFocus !== null
  useEffect(() => {
    if (!open || !hasList) return
    const trigger = refs.domReference.current as Element | null
    if (!trigger?.closest('.task-entry') || trigger.closest('.task-entry--expanded')) return
    return () => restoreListFocusRef.current?.()
  }, [open, hasList, refs.domReference])

  const click = useClick(context)
  // In sheet mode the floating element is never rendered, so floating-ui's
  // outside-press detection would treat every click as "outside" and close
  // immediately — disable it there (the sheet has its own Radix dismiss).
  const dismiss = useDismiss(context, { enabled: !useSheet })
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

   
  const triggerWithRef = React.cloneElement(trigger as React.ReactElement<any>, {
    ref: refs.setReference,
    ...getReferenceProps(),
  })

  // Narrow window + a date dropdown → stacked bottom sheet. The picker content
  // is switched to its sheet layout via the `placement="sheet"` prop. The trigger
  // still toggles `open` (floating useClick), which drives the sheet.
  if (useSheet) {
    return (
      <>
        {triggerWithRef}
        <DateSheetShell open={open} onOpenChange={onOpenChange} title={dateSheet?.title}>
          {React.isValidElement(children)
            ? React.cloneElement(children as React.ReactElement<any>, { placement: 'sheet' })
            : children}
        </DateSheetShell>
      </>
    )
  }

  return (
    <>
      {triggerWithRef}
      {open && backdrop && createPortal(
        <div
          data-task-dropdown-backdrop
          style={{ position: 'fixed', inset: 0, zIndex: 49 }}
          onClick={(e) => { e.stopPropagation(); onOpenChange(false) }}
          onPointerDown={(e) => e.stopPropagation()}
        />,
        document.querySelector('.main-layout__body') ?? document.body
      )}
      {open && createPortal(
        <div
          ref={refs.setFloating}
          data-task-dropdown
          data-placement={finalPlacement}
          className="context-menu-content"
          style={{
            ...floatingStyles,
            ...contentStyle,
            zIndex: 50,
            animation: isPositioned && !instant ? 'task-dropdown-fade-in 0.15s ease-out' : 'none',
            visibility: isPositioned ? 'visible' : 'hidden',
          }}
          {...getFloatingProps()}
        >
          {children}
        </div>,
        document.body
      )}
    </>
  )
}
